<template>
  <div class="appointments-container">
    <main class="main-content">
      <h1 class="title-with-underline">預約資訊</h1>

      <!-- 有預約時顯示 -->
      <div class="appointment-list" id="appointmentList" v-if="appointments.length > 0">
        <div
          v-for="(appointment, index) in appointments"
          :key="appointment.id"
          class="appointment-card"
          :class="{ selected: selectedAppointment && selectedAppointment.id === appointment.id }"
          @click="toggleCard(appointment, index)"
        >
          <div class="appointment-header">
            <div class="appointment-header-content">
              <div class="patient-name">{{ appointment.patientName }}</div>
              <div class="clinic-name">{{ appointment.clinicName }}</div>
              <div class="appointment-summary">
                <div class="summary-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  <span class="summary-value">{{ appointment.dateTime }}</span>
                </div>
                <span class="appointment-type">{{ appointment.type }}</span>
              </div>
            </div>
            <button class="expand-btn" :class="{ expanded: expandedCards[index] }">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
          </div>
          <div class="appointment-details" :class="{ show: expandedCards[index] }">
            <div class="appointment-info">
              <div class="info-row" v-if="appointment.number">
                <span class="info-label">現場號碼</span>
                <span class="info-value highlight">{{ appointment.number }}</span>
              </div>
              <div class="info-row" v-if="appointment.department">
                <span class="info-label">看診科別</span>
                <span class="info-value department">{{ appointment.department }}</span>
              </div>
              <div class="info-row" v-if="appointment.doctor">
                <span class="info-label">看診醫師</span>
                <span class="info-value">{{ appointment.doctor }}</span>
              </div>
              <div class="info-row" v-if="appointment.identity">
                <span class="info-label">看診身分</span>
                <span class="info-value">{{ appointment.identity }}</span>
              </div>
              <div class="info-row" v-if="appointment.checkupItem">
                <span class="info-label">健檢項目</span>
                <span class="info-value">{{ appointment.checkupItem }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">預約狀態</span>
                <span class="info-value status confirmed">{{ appointment.status }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 無預約時顯示 -->
      <div class="empty-state" v-if="appointments.length === 0">
        <div class="empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 6v6l4 2"></path>
          </svg>
        </div>
        <p class="empty-text">您目前沒有預約紀錄</p>
        <button class="btn btn-primary" @click="goToBooking">立即預約</button>
      </div>
    </main>

    <!-- 底部按鈕 -->
    <div class="button-section">
      <div class="button-group">
        <button class="btn btn-secondary" @click="goBack">返回</button>
        <button class="btn btn-danger" :disabled="!selectedAppointment" @click="showCancelModal">取消預約</button>
      </div>
    </div>

    <!-- 取消預約確認 Modal -->
    <div class="tongxin-modal" :class="{ show: showCancelConfirmModal }">
      <div class="tongxin-modal__content">
        <div class="tongxin-modal__header">
          <div class="tongxin-modal__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
          <h2 class="tongxin-modal__title">確認取消預約</h2>
        </div>
        <div class="tongxin-modal__body">
          <p class="tongxin-modal__text">
            確定要取消以下預約嗎？<br>
            取消後將無法復原，需重新預約。
          </p>
          <div class="cancel-info-box" v-if="selectedAppointment">
            <div class="cancel-info-item">
              <span class="cancel-info-label">看診人</span>
              <span class="cancel-info-value highlight">{{ selectedAppointment.patientName }}</span>
            </div>
            <div class="cancel-info-item">
              <span class="cancel-info-label">診所名稱</span>
              <span class="cancel-info-value">{{ selectedAppointment.clinicName }}</span>
            </div>
            <div class="cancel-info-item">
              <span class="cancel-info-label">看診日期</span>
              <span class="cancel-info-value highlight">{{ selectedAppointment.dateTime }}</span>
            </div>
            <div class="cancel-info-item">
              <span class="cancel-info-label">掛號類型</span>
              <span class="cancel-info-value">{{ selectedAppointment.type }}</span>
            </div>
          </div>
        </div>
        <div class="tongxin-modal__buttons">
          <button class="btn btn-secondary" @click="closeModal">取消</button>
          <button class="btn btn-danger" @click="confirmCancel">確認取消</button>
        </div>
      </div>
    </div>

    <!-- 取消結果 Modal -->
    <div class="tongxin-modal" :class="{ show: showResultModal }">
      <div class="tongxin-modal__content">
        <div class="tongxin-modal__header">
          <div class="tongxin-modal__icon result-modal-icon" :class="{ success: resultSuccess, error: !resultSuccess }">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <template v-if="resultSuccess">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </template>
              <template v-else>
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </template>
            </svg>
          </div>
          <h2 class="tongxin-modal__title">{{ resultTitle }}</h2>
        </div>
        <div class="tongxin-modal__body">
          <p class="tongxin-modal__text">{{ resultMessage }}</p>
          <div class="error-reason" v-if="errorReason">
            <div class="error-reason-title">錯誤原因</div>
            <div class="error-reason-text">{{ errorReason }}</div>
          </div>
        </div>
        <div class="tongxin-modal__buttons">
          <button class="btn btn-primary" @click="closeResultModal" style="flex-grow: 1">確定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Appointments",
  data() {
    return {
      appointments: [
        {
          id: 1,
          patientName: "王小明 (本人)",
          clinicName: "姜博文診所",
          dateTime: "9/11 (三) 夜診",
          type: "一般看診",
          number: "20 號",
          department: "家醫科",
          doctor: "黃柏維醫師",
          identity: "一般看診",
          status: "已確認"
        },
        {
          id: 2,
          patientName: "王小明 (本人)",
          clinicName: "姜博文診所",
          dateTime: "9/15 (日) 上午診",
          type: "體檢健檢",
          number: "現場掛號",
          department: "體檢科",
          checkupItem: "青春活力健康基礎",
          status: "已確認"
        }
      ],
      expandedCards: {},
      selectedAppointment: null,
      showCancelConfirmModal: false,
      showResultModal: false,
      resultSuccess: true,
      resultTitle: "",
      resultMessage: "",
      errorReason: ""
    };
  },
  methods: {
    toggleCard(appointment, index) {
      // 切換展開狀態
      this.$set(this.expandedCards, index, !this.expandedCards[index]);

      // 選中卡片
      this.selectCard(appointment);
    },
    selectCard(appointment) {
      this.selectedAppointment = appointment;
    },
    showCancelModal() {
      if (!this.selectedAppointment) {
        alert('請先選擇要取消的預約');
        return;
      }
      this.showCancelConfirmModal = true;
    },
    closeModal() {
      this.showCancelConfirmModal = false;
    },
    confirmCancel() {
      this.closeModal();

      // 模擬 API 呼叫，這裡可以替換成真實的 API 請求
      // 隨機模擬成功或失敗（實際使用時移除隨機邏輯）
      const isSuccess = Math.random() > 0.3; // 70% 成功率，測試用

      if (isSuccess) {
        // 取消成功
        this.showResultWithSuccess('您的預約已成功取消');

        // 移除卡片
        this.appointments = this.appointments.filter(app => app.id !== this.selectedAppointment.id);
        this.selectedAppointment = null;
      } else {
        // 取消失敗
        const errorReason = '系統忙碌中，請稍後再試。若問題持續發生，請聯繫客服人員。';
        this.showResultWithError('預約取消失敗', errorReason);
      }
    },
    showResultWithSuccess(message) {
      this.resultSuccess = true;
      this.resultTitle = "取消成功";
      this.resultMessage = message;
      this.errorReason = "";
      this.showResultModal = true;
    },
    showResultWithError(message, reason) {
      this.resultSuccess = false;
      this.resultTitle = "取消失敗";
      this.resultMessage = message;
      this.errorReason = reason;
      this.showResultModal = true;
    },
    closeResultModal() {
      this.showResultModal = false;
    },
    goToBooking() {
      // 導航到預約頁面
      this.$router.push({ name: 'TongxinStep', params: { stepId: 'exam-selection' } });
    },
    goBack() {
      this.$router.go(-1);
    }
  }
};
</script>

<style lang="scss" scoped>
.button-group {
  display: flex;
  justify-content: center;
  gap: 20px;

  .btn {
    flex: 1;
  }

  .btn + .btn {
    margin-left: auto;
  }
}

.appointments-container {
  position: relative;
  min-height: 100vh;
}

.main-content {
  padding: 32px;
  min-height: calc(100vh - 200px);
  max-width: 800px;
  margin: 0 auto;
  overflow-x: hidden;
}

/* 預約卡片列表 */
.appointment-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 24px;
}

.appointment-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border: 2px solid #e9ecef;
  transition: all 0.3s;
  position: relative;
  cursor: pointer;
}

.appointment-card:hover {
  border-color: #2a9d8f;
  box-shadow: 0 4px 12px rgba(42, 157, 143, 0.15);
}

.appointment-card.selected {
  background: #e8f5f3;
  border-color: #2a9d8f;
}

.appointment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  position: relative;
}

.appointment-header-content {
  flex: 1;
}

.patient-name {
  font-size: 20px;
  font-weight: bold;
  color: #207868;
  margin-bottom: 4px;
}

.clinic-name {
  font-size: 20px;
  color: #666;
  margin-bottom: 8px;
  font-weight: bold;
}

.appointment-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

@media (min-width: 375px) {
  .appointment-summary {
    gap: 12px;
  }
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 17px;
  color: #666;
}

.summary-item svg {
  width: 20px;
  height: 20px;
  color: #2a9d8f;
}

.summary-value {
  font-weight: 600;
  color: #333;
}

.appointment-type {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 700;
  background: #e8f5f3;
  color: #207868;
}

.expand-btn {
  position: absolute;
  top: 0;
  right: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s;
  flex-shrink: 0;
}

.expand-btn svg {
  width: 24px;
  height: 24px;
  color: #2a9d8f;
}

.expand-btn.expanded svg {
  transform: rotate(180deg);
}

.appointment-details {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease, margin-top 0.3s ease;
  opacity: 0;
  width: 100%;
}

.appointment-details.show {
  max-height: 500px;
  opacity: 1;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 2px solid #e9ecef;
  overflow: hidden;
}

.appointment-info {
  display: grid;
  gap: 12px;
  width: 100%;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  word-break: break-word;
}

.info-label {
  font-size: 20px;
  color: #666;
  font-weight: 500;
  flex-shrink: 0;
  margin-right: 12px;
}

.info-value {
  font-size: 20px;
  color: #333;
  font-weight: 600;
  text-align: right;
  word-break: break-word;
}

.info-value.highlight {
  color: #207868;
  font-weight: 700;
}

.info-value.department {
  color: #8b5cf6;
  font-weight: 700;
}

.info-value.status {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 700;
}

.info-value.status.confirmed {
  background: #e8f5f3;
  color: #207868;
}

/* 空狀態 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  width: 100px;
  height: 100px;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.empty-icon svg {
  width: 50px;
  height: 50px;
  color: #ccc;
}

.empty-text {
  font-size: 20px;
  color: #999;
  margin-bottom: 24px;
}

/* 底部按鈕 */
.button-section {
  position: sticky;
  bottom: 0;
  background: #fff;
  padding: 24px 32px;
  border-top: 1px solid #e9ecef;
  box-shadow: 0 -5px 20px rgba(0,0,0,.1);
}

/* Modal */
.tongxin-modal {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  z-index: 2000;
  justify-content: center;
  align-items: center;

  &.show {
    display: flex;
  }

  &__content {
    background: #fff;
    border-radius: 16px;
    padding: 32px;
    max-width: 500px;
    width: 90%;
    box-shadow: 0 10px 40px rgba(0,0,0,0.3);
    animation: modalSlideIn 0.3s ease-out;
  }

  &__header {
    text-align: center;
    margin-bottom: 24px;
  }

  &__icon {
    width: 60px;
    height: 60px;
    background: #fee2e2;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
  }

  &__icon svg {
    width: 30px;
    height: 30px;
    color: #dc2626;
  }

  &__title {
    font-size: 26px;
    font-weight: bold;
    color: #333;
    margin-bottom: 8px;
  }

  &__body {
    margin-bottom: 24px;
  }

  &__text {
    font-size: 20px;
    color: #666;
    line-height: 1.6;
    text-align: center;
    margin-bottom: 20px;
  }

  &__buttons {
    display: flex;
    justify-content: center;
    gap: 12px;

    .btn + .btn {
      margin-left: auto;
    }
  }
}

.cancel-info-box {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  border-left: 4px solid #dc2626;
}

.cancel-info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
}

.cancel-info-item:last-child {
  border-bottom: none;
}

.cancel-info-label {
  font-size: 16px;
  color: #666;
  font-weight: 500;
}

.cancel-info-value {
  font-size: 19px;
  color: #333;
  font-weight: 600;
  text-align: right;
}

.cancel-info-value.highlight {
  color: #dc2626;
  font-weight: 700;
}

/* 結果 Modal 樣式 */
.result-modal-icon.success {
  background: #d1fae5;
}

.result-modal-icon.success svg {
  color: #10b981;
}

.result-modal-icon.error {
  background: #fee2e2;
}

.result-modal-icon.error svg {
  color: #dc2626;
}

.error-reason {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
  text-align: left;
}

.error-reason-title {
  font-size: 16px;
  font-weight: bold;
  color: #dc2626;
  margin-bottom: 8px;
}

.error-reason-text {
  font-size: 16px;
  color: #991b1b;
  line-height: 1.6;
}

@media (max-width:767px) {
  .main-content {
    padding: 20px;
  }

  .page-title {
    font-size: 24px;
  }

  .appointment-card {
    padding: 16px;
  }

  .patient-name {
    font-size: 20px;
  }

  .clinic-name {
    font-size: 20px;
  }

  .summary-item {
    font-size: 16px;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .info-value {
    text-align: left;
  }

  .button-section {
    padding: 16px;
  }

  .modal-content {
    padding: 24px;
  }

  .modal-title {
    font-size: 22px;
  }

  .cancel-info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 10px 0;
  }

  .cancel-info-label {
    font-size: 15px;
  }

  .cancel-info-value {
    font-size: 19px;
    text-align: left;
  }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
